import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';

import { ApiService } from '../../core/services/api.service';
import { IssueWorkflowApiService } from './issue-workflow.service';
import {
  IssueWorkflowDetailDto,
  IssueWorkflowDto,
  IssueWorkflowTransitionDto
} from './issue-workflow.models';

interface ProjectRow { id: string; name: string; }
interface LayoutPos { posX: number; posY: number; }

@Component({
  selector: 'app-issue-workflows',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule,
    MatFormFieldModule, MatSelectModule, MatInputModule, MatButtonModule,
    MatIconModule, MatProgressSpinnerModule, MatCheckboxModule, MatChipsModule,
    MatTooltipModule, MatExpansionModule
  ],
  templateUrl: './issue-workflows.component.html',
  styleUrl: './issue-workflows.component.scss'
})
export class IssueWorkflowsComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly workflowsApi = inject(IssueWorkflowApiService);
  private readonly route = inject(ActivatedRoute);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly projects = signal<ProjectRow[]>([]);
  readonly workflows = signal<IssueWorkflowDto[]>([]);
  readonly detail = signal<IssueWorkflowDetailDto | null>(null);
  readonly selectedProjectId = signal<string | null>(null);
  readonly selectedWorkflowId = signal<string | null>(null);
  readonly layoutPositions = signal<Map<string, LayoutPos>>(new Map());
  readonly savingLayout = signal(false);

  readonly designerWidth = 720;
  readonly designerHeight = 1280;
  readonly nodeWidth = 168;
  readonly nodeHeight = 44;

  private dragging: { statusId: string; offsetX: number; offsetY: number } | null = null;

  newName = '';
  newStatusName = '';
  newStatusCategory = 0;
  newStatusMapped = 0;
  newTransitionName = '';
  newFromStatusId = '';
  newToStatusId = '';
  selectedTransitionId = '';
  newConditionType = 0;
  newValidatorType = 1;
  newActionType = 0;

  readonly categoryOptions = [
    { value: 0, label: 'To Do' },
    { value: 1, label: 'In Progress' },
    { value: 2, label: 'Done' }
  ];
  readonly mappedStatusOptions = [
    { value: 0, label: 'Open' },
    { value: 1, label: 'In Progress' },
    { value: 3, label: 'Done' },
    { value: 4, label: 'Closed' },
    { value: 5, label: 'Reopened' },
    { value: 6, label: 'Blocked' },
    { value: 7, label: 'Overdue' },
    { value: 8, label: 'Draft' },
    { value: 9, label: 'On Hold' },
    { value: 10, label: 'Cancelled' },
    { value: 11, label: 'To Do' },
    { value: 12, label: 'QA' }
  ];
  readonly conditionOptions = [
    { value: 0, label: 'Has assignee' },
    { value: 1, label: 'Field not empty' },
    { value: 2, label: 'Issue type in' },
    { value: 3, label: 'No open blockers' }
  ];
  readonly validatorOptions = [
    { value: 0, label: 'Required field' },
    { value: 1, label: 'Comment required' },
    { value: 2, label: 'Approval comment' }
  ];
  readonly actionOptions = [
    { value: 0, label: 'Set field' },
    { value: 1, label: 'Clear field' },
    { value: 2, label: 'Assign to current user' },
    { value: 3, label: 'Add activity note' },
    { value: 4, label: 'Notify assignee' },
    { value: 5, label: 'Clear overdue' },
    { value: 6, label: 'Request approval' },
    { value: 7, label: 'Send email' }
  ];

  ngOnInit(): void {
    const projectIdFromQuery = this.route.snapshot.queryParamMap.get('projectId');
    this.api.getPaged<ProjectRow>('v1/Projects', { page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        this.projects.set(page.items ?? []);
        const preferred = projectIdFromQuery && page.items?.some(p => p.id === projectIdFromQuery)
          ? projectIdFromQuery
          : page.items?.[0]?.id;
        if (preferred) this.selectProject(preferred);
      },
      error: (err) => this.error.set(err?.message || 'Failed to load projects.')
    });
  }

  selectProject(projectId: string): void {
    this.selectedProjectId.set(projectId);
    this.selectedWorkflowId.set(null);
    this.detail.set(null);
    this.loadWorkflows(projectId);
  }

  loadWorkflows(projectId: string): void {
    this.loading.set(true);
    this.workflowsApi.list(projectId).subscribe({
      next: (items) => {
        const list = Array.isArray(items) ? items : [];
        this.workflows.set(list);
        this.loading.set(false);
        const preferred = list.find(w => w.isDefault) ?? list[0];
        if (preferred) this.selectWorkflow(preferred.id);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load workflows.');
      }
    });
  }

  selectWorkflow(id: string): void {
    this.selectedWorkflowId.set(id);
    this.loading.set(true);
    this.workflowsApi.get(id).subscribe({
      next: (detail) => {
        this.detail.set(detail);
        this.initLayoutPositions(detail);
        this.selectedTransitionId = detail.transitions?.[0]?.id ?? '';
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load workflow.');
      }
    });
  }

  createWorkflow(): void {
    const projectId = this.selectedProjectId();
    if (!projectId || !this.newName.trim()) return;
    this.workflowsApi.create({
      name: this.newName.trim(),
      projectId,
      isDefault: this.workflows().length === 0,
      createDefaultScheme: true,
      createProcessScheme: false
    }).subscribe({
      next: (wf) => {
        this.newName = '';
        this.loadWorkflows(projectId);
        if (wf?.id) this.selectWorkflow(wf.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to create workflow.')
    });
  }

  /** Creates the planning → design → ASO → dev → QA → completion process scheme from the product flowchart. */
  createProcessWorkflow(): void {
    const projectId = this.selectedProjectId();
    if (!projectId) {
      this.error.set('Select a project first.');
      return;
    }
    const name = this.newName.trim() || 'Product Process Workflow';
    this.workflowsApi.create({
      name,
      projectId,
      description: 'Planning, design gate, ASO, development, QA, and completion with approval loops.',
      isDefault: this.workflows().length === 0,
      createDefaultScheme: false,
      createProcessScheme: true
    }).subscribe({
      next: (wf) => {
        this.newName = '';
        this.loadWorkflows(projectId);
        if (wf?.id) this.selectWorkflow(wf.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to create process workflow.')
    });
  }

  addStatus(): void {
    const id = this.selectedWorkflowId();
    if (!id || !this.newStatusName.trim()) return;
    this.workflowsApi.addStatus(id, {
      name: this.newStatusName.trim(),
      category: this.newStatusCategory,
      mappedIssueStatus: this.newStatusMapped
    }).subscribe({
      next: () => {
        this.newStatusName = '';
        this.selectWorkflow(id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to add status.')
    });
  }

  removeStatus(statusId: string): void {
    const id = this.selectedWorkflowId();
    if (!id || !confirm('Delete this status? Related transitions may be affected.')) return;
    this.workflowsApi.deleteStatus(id, statusId).subscribe({
      next: () => this.selectWorkflow(id),
      error: (err) => this.error.set(err?.message || 'Failed to delete status.')
    });
  }

  addTransition(): void {
    const id = this.selectedWorkflowId();
    if (!id || !this.newTransitionName.trim() || !this.newToStatusId) return;
    this.workflowsApi.addTransition(id, {
      name: this.newTransitionName.trim(),
      toStatusId: this.newToStatusId,
      fromStatusId: this.newFromStatusId || undefined
    }).subscribe({
      next: () => {
        this.newTransitionName = '';
        this.selectWorkflow(id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to add transition.')
    });
  }

  removeTransition(transitionId: string): void {
    const id = this.selectedWorkflowId();
    if (!id || !confirm('Delete this transition and its rules?')) return;
    this.workflowsApi.deleteTransition(id, transitionId).subscribe({
      next: () => {
        if (this.selectedTransitionId === transitionId) this.selectedTransitionId = '';
        this.selectWorkflow(id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to delete transition.')
    });
  }

  addCondition(): void {
    if (!this.selectedTransitionId) return;
    const id = this.selectedWorkflowId();
    this.workflowsApi.addCondition(this.selectedTransitionId, {
      conditionType: this.newConditionType,
      failureMessage: this.newConditionType === 0 ? 'Assignee is required.' : undefined,
      configJson: this.newConditionType === 1 ? '{"field":"Description"}' : undefined
    }).subscribe({
      next: () => id && this.selectWorkflow(id),
      error: (err) => this.error.set(err?.message || 'Failed to add condition.')
    });
  }

  removeCondition(conditionId: string): void {
    const id = this.selectedWorkflowId();
    if (!this.selectedTransitionId) return;
    this.workflowsApi.deleteCondition(this.selectedTransitionId, conditionId).subscribe({
      next: () => id && this.selectWorkflow(id),
      error: (err) => this.error.set(err?.message || 'Failed to delete condition.')
    });
  }

  addValidator(): void {
    if (!this.selectedTransitionId) return;
    const id = this.selectedWorkflowId();
    this.workflowsApi.addValidator(this.selectedTransitionId, {
      validatorType: this.newValidatorType,
      failureMessage: this.newValidatorType === 1 ? 'Comment is required.' : 'Required field missing.'
    }).subscribe({
      next: () => id && this.selectWorkflow(id),
      error: (err) => this.error.set(err?.message || 'Failed to add validator.')
    });
  }

  removeValidator(validatorId: string): void {
    const id = this.selectedWorkflowId();
    if (!this.selectedTransitionId) return;
    this.workflowsApi.deleteValidator(this.selectedTransitionId, validatorId).subscribe({
      next: () => id && this.selectWorkflow(id),
      error: (err) => this.error.set(err?.message || 'Failed to delete validator.')
    });
  }

  addAction(): void {
    if (!this.selectedTransitionId) return;
    const id = this.selectedWorkflowId();
    let configJson: string | undefined;
    if (this.newActionType === 3) {
      configJson = '{"message":"Transition completed."}';
    } else if (this.newActionType === 7) {
      configJson = JSON.stringify({
        to: 'assignee',
        subject: 'Workflow transition',
        body: 'An issue you are involved in was transitioned.'
      });
    }
    this.workflowsApi.addAction(this.selectedTransitionId, {
      actionType: this.newActionType,
      configJson
    }).subscribe({
      next: () => id && this.selectWorkflow(id),
      error: (err) => this.error.set(err?.message || 'Failed to add action.')
    });
  }

  removeAction(actionId: string): void {
    const id = this.selectedWorkflowId();
    if (!this.selectedTransitionId) return;
    this.workflowsApi.deleteAction(this.selectedTransitionId, actionId).subscribe({
      next: () => id && this.selectWorkflow(id),
      error: (err) => this.error.set(err?.message || 'Failed to delete action.')
    });
  }

  selectedTransition(): IssueWorkflowTransitionDto | undefined {
    return this.detail()?.transitions?.find(t => t.id === this.selectedTransitionId);
  }

  initLayoutPositions(detail: IssueWorkflowDetailDto): void {
    const positions = new Map<string, LayoutPos>();
    const statuses = detail.statuses ?? [];
    const cols = Math.max(1, Math.ceil(Math.sqrt(statuses.length)));
    statuses.forEach((s, i) => {
      if (s.posX != null && s.posY != null) {
        positions.set(s.id, { posX: s.posX, posY: s.posY });
      } else {
        const col = i % cols;
        const row = Math.floor(i / cols);
        positions.set(s.id, {
          posX: 40 + col * (this.nodeWidth + 48),
          posY: 40 + row * (this.nodeHeight + 56)
        });
      }
    });
    this.layoutPositions.set(positions);
  }

  nodePos(statusId: string): LayoutPos {
    return this.layoutPositions().get(statusId) ?? { posX: 0, posY: 0 };
  }

  nodeCenter(statusId: string): { x: number; y: number } {
    const pos = this.nodePos(statusId);
    return { x: pos.posX + this.nodeWidth / 2, y: pos.posY + this.nodeHeight / 2 };
  }

  transitionEdges(): { x1: number; y1: number; x2: number; y2: number; name: string }[] {
    const detail = this.detail();
    if (!detail) return [];
    return detail.transitions
      .filter(t => t.fromStatusId)
      .map(t => {
        const from = this.nodeCenter(t.fromStatusId!);
        const to = this.nodeCenter(t.toStatusId);
        return { x1: from.x, y1: from.y, x2: to.x, y2: to.y, name: t.name };
      });
  }

  onNodeMouseDown(event: MouseEvent, statusId: string): void {
    event.preventDefault();
    const pos = this.layoutPositions().get(statusId);
    if (!pos) return;
    const pt = this.clientToSvg(event);
    this.dragging = { statusId, offsetX: pt.x - pos.posX, offsetY: pt.y - pos.posY };
  }

  onDesignerMouseMove(event: MouseEvent): void {
    if (!this.dragging) return;
    const pt = this.clientToSvg(event);
    const next = new Map(this.layoutPositions());
    next.set(this.dragging.statusId, {
      posX: Math.max(0, Math.min(this.designerWidth - this.nodeWidth, pt.x - this.dragging.offsetX)),
      posY: Math.max(0, Math.min(this.designerHeight - this.nodeHeight, pt.y - this.dragging.offsetY))
    });
    this.layoutPositions.set(next);
  }

  onDesignerMouseUp(): void {
    this.dragging = null;
  }

  private clientToSvg(event: MouseEvent): { x: number; y: number } {
    const svg = (event.currentTarget as Element).closest('svg') ?? event.target as Element;
    const el = svg instanceof SVGSVGElement ? svg : document.querySelector('.wf-designer svg');
    if (!el || !(el instanceof SVGSVGElement)) {
      return { x: event.offsetX, y: event.offsetY };
    }
    const pt = el.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    const ctm = el.getScreenCTM();
    if (!ctm) return { x: event.offsetX, y: event.offsetY };
    const transformed = pt.matrixTransform(ctm.inverse());
    return { x: transformed.x, y: transformed.y };
  }

  saveLayout(): void {
    const workflowId = this.selectedWorkflowId();
    const detail = this.detail();
    if (!workflowId || !detail) return;
    const positions = this.layoutPositions();
    const statuses = detail.statuses.map(s => {
      const p = positions.get(s.id) ?? { posX: 0, posY: 0 };
      return { statusId: s.id, posX: p.posX, posY: p.posY };
    });
    this.savingLayout.set(true);
    this.workflowsApi.updateLayout(workflowId, { statuses }).subscribe({
      next: () => {
        this.savingLayout.set(false);
        this.selectWorkflow(workflowId);
      },
      error: (err) => {
        this.savingLayout.set(false);
        this.error.set(err?.message || 'Failed to save layout.');
      }
    });
  }
}
